import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useTheme } from '../contexts/ThemeContext'
import { useCloudStorage } from '../hooks/useCloudStorage'
import CustomerGoogleDriveConnect from '../components/onboarding/CustomerGoogleDriveConnect'
import { 
  ArrowRight, 
  ArrowLeft, 
  Check, 
  Cloud, 
  HardDrive, 
  Shield, 
  Zap,
  DollarSign,
  Users,
  Star,
  ExternalLink
} from 'lucide-react'
import toast from 'react-hot-toast'

/**
 * User Onboarding Page
 * Multi-step onboarding flow for new users to select plans and configure storage
 */
const OnboardingPage = () => {
  const [currentStep, setCurrentStep] = useState(1)
  const [selectedPlan, setSelectedPlan] = useState(() => {
    // Load from localStorage on initialization
    return localStorage.getItem('onboarding_selectedPlan') || null
  })
  const [selectedProvider, setSelectedProvider] = useState(null)
  const [isCompleting, setIsCompleting] = useState(false)
  const [hasCompleted, setHasCompleted] = useState(false) // Prevent auto-advance after completion

  const navigate = useNavigate()
  const { user, updateUser, logout } = useAuth()
  const { currentTheme, setTheme } = useTheme()
  const { isConnected, connectedProvider } = useCloudStorage()

  // Debug logging for CloudStorage state
  console.log('🔍 OnboardingPage: CloudStorage state check')
  console.log('- isConnected:', isConnected)
  console.log('- connectedProvider:', connectedProvider)
  console.log('- localStorage cloudProvider:', localStorage.getItem('cloudProvider'))
  console.log('- localStorage cloudUserInfo:', localStorage.getItem('cloudUserInfo'))
  console.log('- hasCompleted:', hasCompleted)

  // Force light mode for onboarding page - captive portal should never use dark mode
  useEffect(() => {
    const previousTheme = currentTheme
    if (currentTheme !== 'light') {
      setTheme('light')
    }
    return () => {
      // Restore previous theme when leaving the page
      if (previousTheme !== 'light') {
        setTheme(previousTheme)
      }
    }
  }, [])

  // Persist selectedPlan to localStorage
  useEffect(() => {
    if (selectedPlan) {
      localStorage.setItem('onboarding_selectedPlan', selectedPlan)
    }
  }, [selectedPlan])

  // Redirect to login if user is not authenticated
  useEffect(() => {
    if (!user) {
      console.log('User not authenticated, redirecting to login')
      navigate('/login', { replace: true })
    }
  }, [user, navigate])

  // Auto-advance to completion step when Google Drive is connected
  useEffect(() => {
    console.log('🔍 OnboardingPage: Checking auto-advance conditions')
    console.log('- currentStep:', currentStep)
    console.log('- selectedPlan:', selectedPlan)
    console.log('- isConnected:', isConnected)
    console.log('- hasCompleted:', hasCompleted)
    
    // If user has selected free plan and Google Drive is connected, go to final step
    // But only if we haven't already completed onboarding
    if (selectedPlan === 'free' && isConnected && currentStep < 4 && !hasCompleted) {
      console.log('✅ OnboardingPage: Google Drive connection detected, advancing to final step')
      toast.success('Google Drive connected successfully!')
      setCurrentStep(4)
    }
  }, [currentStep, selectedPlan, isConnected, hasCompleted])

  // Don't render if user is not authenticated
  if (!user) {
    return null
  }

  // Check if Google Client ID is configured
  const isGoogleConfigured = import.meta.env.VITE_GOOGLE_CLIENT_ID && 
    import.meta.env.VITE_GOOGLE_CLIENT_ID !== 'your-google-client-id' &&
    import.meta.env.VITE_GOOGLE_CLIENT_ID.includes('.apps.googleusercontent.com')

  const steps = [
    { id: 1, title: 'Welcome', description: 'Get started with your notes app' },
    { id: 2, title: 'Choose Plan', description: 'Select the plan that fits your needs' },
    { id: 3, title: 'Connect Storage', description: 'Connect your Google Drive (for Free plan)' },
    { id: 4, title: 'All Set', description: 'Start organizing your notes' }
  ]

  const plans = [
    {
      id: 'free',
      name: 'Free Plan',
      price: 'Free',
      description: 'Use your own cloud storage',
      features: [
        'Unlimited notes in your cloud storage',
        'End-to-end encryption',
        'Access from any device',
        'Your data, your control',
        'Google Drive, Dropbox, OneDrive support'
      ],
      icon: Cloud,
      color: 'blue',
      recommended: true
    },
    {
      id: 'basic',
      name: 'Basic Plan',
      price: '$1/month',
      description: 'Managed cloud storage with 1GB',
      features: [
        '1GB managed cloud storage',
        'Automatic backups',
        'Priority support',
        'Advanced sync features',
        'No setup required'
      ],
      icon: HardDrive,
      color: 'green'
    },
    {
      id: 'pro',
      name: 'Pro Plan',
      price: '$5/month',
      description: 'Professional features with 5GB+ storage',
      features: [
        '5GB managed cloud storage',
        'Additional storage: $1/GB',
        'Team collaboration features',
        'Advanced AI integrations',
        'Premium support',
        'API access'
      ],
      icon: Star,
      color: 'purple'
    }
  ]

  const cloudProviders = [
    {
      id: 'google_drive',
      name: 'Google Drive',
      description: 'Store your notes in Google Drive',
      icon: '🗄️',
      supported: true
    },
    {
      id: 'dropbox',
      name: 'Dropbox',
      description: 'Store your notes in Dropbox',
      icon: '📦',
      supported: false
    },
    {
      id: 'onedrive',
      name: 'OneDrive',
      description: 'Store your notes in OneDrive',
      icon: '☁️',
      supported: false
    }
  ]

  /**
   * Handle plan selection
   */
  const handlePlanSelect = (planId) => {
    setSelectedPlan(planId)
    console.log('📋 Onboarding: Plan selected:', planId)
  }

  /**
   * Handle cloud provider selection
   */
  const handleProviderSelect = (providerId) => {
    setSelectedProvider(providerId)
    console.log('☁️ Onboarding: Provider selected:', providerId)
  }

  /**
   * Handle successful cloud connection
   */
  const handleCloudConnectionSuccess = (data) => {
    console.log('✅ Onboarding: Cloud connected successfully')
    toast.success('Google Drive connected successfully!')
    setCurrentStep(4) // Go to final step
  }

  /**
   * Handle cloud connection error
   */
  const handleCloudConnectionError = (error) => {
    console.error('❌ Onboarding: Cloud connection failed:', error)
    toast.error('Failed to connect cloud storage')
  }

  /**
   * Handle successful provider setup
   */
  const handleProviderSetupComplete = (result) => {
    console.log('✅ Onboarding: Provider setup completed')
    toast.success('Provider configured successfully!')
    setCurrentStep(4) // Move to authentication step
  }

  /**
   * Handle provider setup error
   */
  const handleProviderSetupError = (error) => {
    console.error('❌ Onboarding: Provider setup failed:', error)
    toast.error('Failed to configure provider')
  }

  /**
   * Complete onboarding
   */
  const completeOnboarding = async () => {
    try {
      setIsCompleting(true)
      console.log('🎯 Onboarding: Completing onboarding process')

      // Send onboarding completion to backend
      const authToken = localStorage.getItem('authToken')
      if (!authToken) {
        throw new Error('No authentication token found')
      }

      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080'
      const response = await fetch(`${API_BASE_URL}/api/onboarding/complete`, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${authToken}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to complete onboarding')
      }

      const result = await response.json()
      console.log('✅ Onboarding: Backend completion response:', result)

      console.log('✅ Onboarding: Onboarding completed successfully')
      toast.success('Welcome to your notes app!')
      
      // Update user to mark onboarding as complete
      console.log('🔄 OnboardingPage: Updating user needsOnboarding to false')
      updateUser({
        ...user,
        needsOnboarding: false
      })
      
      // Clear onboarding data from localStorage
      localStorage.removeItem('onboarding_selectedPlan')
      setHasCompleted(true) // Mark as completed
      
      // Navigate to dashboard
      console.log('🚀 OnboardingPage: Navigating to /notes...')
      navigate('/notes', { replace: true })
      console.log('✅ OnboardingPage: Navigation call completed')

    } catch (error) {
      console.error('❌ Onboarding: Failed to complete onboarding:', error)
      toast.error('Failed to complete setup: ' + error.message)
    } finally {
      setIsCompleting(false)
    }
  }

  /**
   * Skip onboarding (for now)
   */
  const skipOnboarding = async () => {
    console.log('⏭️ Onboarding: Skipping onboarding')
    try {
      await logout()
      navigate('/login', { replace: true })
    } catch (error) {
      console.error('Error during logout:', error)
      // Still navigate to login even if logout fails
      navigate('/login', { replace: true })
    }
  }

  /**
   * Navigate to next step
   */
  const nextStep = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1)
    }
  }

  /**
   * Navigate to previous step
   */
  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    }
  }

  /**
   * Check if current step can proceed
   */
  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return true
      case 2:
        return selectedPlan !== null
      case 3:
        return selectedPlan === 'free' ? isConnected : true
      case 4:
        return true
      default:
        return false
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-2 sm:p-4">
      <div className="bg-white rounded-lg sm:rounded-xl shadow-xl w-full max-w-4xl max-h-[98vh] sm:max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-4 sm:p-6 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
            <div className="text-center sm:text-left">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Welcome to Notes App</h1>
              <p className="text-sm sm:text-base text-gray-600 mt-1">Let's get you set up in just a few steps</p>
            </div>
            <button
              onClick={skipOnboarding}
              className="text-sm text-gray-500 hover:text-gray-700 underline self-center sm:self-auto"
            >
              Skip for now
            </button>
          </div>

          {/* Progress Steps */}
          <div className="mt-4 sm:mt-6">
            <div className="flex items-center justify-between">
              {steps.map((step, index) => (
                <div key={step.id} className="flex items-center">
                  <div className={`flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full border-2 ${
                    currentStep >= step.id 
                      ? 'bg-blue-600 border-blue-600 text-white' 
                      : 'border-gray-300 text-gray-500'
                  }`}>
                    {currentStep > step.id ? (
                      <Check className="w-4 h-4 sm:w-5 sm:h-5" />
                    ) : (
                      <span className="text-xs sm:text-sm font-medium">{step.id}</span>
                    )}
                  </div>
                  {index < steps.length - 1 && (
                    <div className={`w-8 sm:w-16 h-0.5 mx-1 sm:mx-2 ${
                      currentStep > step.id ? 'bg-blue-600' : 'bg-gray-300'
                    }`} />
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-between mt-2 px-1">
              {steps.map((step) => (
                <div key={step.id} className="text-center max-w-[70px] sm:max-w-none">
                  <p className="text-xs font-medium text-gray-900 truncate sm:truncate-none">{step.title}</p>
                  <p className="text-xs text-gray-500 hidden sm:block">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Step Content */}
        <div className="p-4 sm:p-6">
          {/* Step 1: Welcome */}
          {currentStep === 1 && (
            <div className="text-center">
              <div className="w-20 h-20 sm:w-24 sm:h-24 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                <Shield className="w-10 h-10 sm:w-12 sm:h-12 text-blue-600" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">
                Your notes, your way
              </h2>
              <p className="text-base sm:text-lg text-gray-600 mb-6 max-w-2xl mx-auto px-2">
                Welcome to the most secure and flexible notes application. Choose how you want to store 
                and sync your notes - we put you in control of your data.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 max-w-3xl mx-auto">
                <div className="bg-blue-50 p-3 sm:p-4 rounded-lg">
                  <Shield className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600 mx-auto mb-2" />
                  <h3 className="font-medium text-gray-900 text-sm sm:text-base">Secure</h3>
                  <p className="text-xs sm:text-sm text-gray-600">End-to-end encryption</p>
                </div>
                <div className="bg-green-50 p-3 sm:p-4 rounded-lg">
                  <Cloud className="w-6 h-6 sm:w-8 sm:h-8 text-green-600 mx-auto mb-2" />
                  <h3 className="font-medium text-gray-900 text-sm sm:text-base">Flexible</h3>
                  <p className="text-xs sm:text-sm text-gray-600">Your choice of storage</p>
                </div>
                <div className="bg-purple-50 p-3 sm:p-4 rounded-lg">
                  <Zap className="w-6 h-6 sm:w-8 sm:h-8 text-purple-600 mx-auto mb-2" />
                  <h3 className="font-medium text-gray-900 text-sm sm:text-base">Fast</h3>
                  <p className="text-xs sm:text-sm text-gray-600">Instant sync & access</p>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Plan Selection */}
          {currentStep === 2 && (
            <div>
              <div className="text-center mb-6 sm:mb-8">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">Choose Your Plan</h2>
                <p className="text-sm sm:text-base text-gray-600 px-2">
                  Select the plan that best fits your needs. You can change this later.
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
                {plans.map((plan) => {
                  const IconComponent = plan.icon
                  const isSelected = selectedPlan === plan.id
                  
                  return (
                    <div
                      key={plan.id}
                      onClick={() => handlePlanSelect(plan.id)}
                      className={`relative border-2 rounded-lg p-4 sm:p-6 cursor-pointer transition-all ${
                        isSelected 
                          ? 'border-blue-500 bg-blue-50' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {plan.recommended && (
                        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                          <span className="bg-blue-500 text-white text-xs font-medium px-2 sm:px-3 py-1 rounded-full">
                            Recommended
                          </span>
                        </div>
                      )}
                      
                      <div className="text-center">
                        <div className={`w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3 sm:mb-4 rounded-full flex items-center justify-center bg-${plan.color}-100`}>
                          <IconComponent className={`w-5 h-5 sm:w-6 sm:h-6 text-${plan.color}-600`} />
                        </div>
                        
                        <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                        <p className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">{plan.price}</p>
                        <p className="text-sm sm:text-base text-gray-600 mb-3 sm:mb-4">{plan.description}</p>
                        
                        <ul className="text-xs sm:text-sm text-gray-600 space-y-1 sm:space-y-2 text-left">
                          {plan.features.map((feature, index) => (
                            <li key={index} className="flex items-start">
                              <Check className="w-3 h-3 sm:w-4 sm:h-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                              <span className="leading-tight">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      {isSelected && (
                        <div className="absolute top-3 right-3 sm:top-4 sm:right-4">
                          <div className="w-5 h-5 sm:w-6 sm:h-6 bg-blue-500 rounded-full flex items-center justify-center">
                            <Check className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Step 3: Connect Google Drive (for Free plan) */}
          {currentStep === 3 && selectedPlan === 'free' && (
            <div>
              <div className="text-center mb-6 sm:mb-8">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">Connect Your Google Drive</h2>
                <p className="text-sm sm:text-base text-gray-600 max-w-md mx-auto px-2">
                  Store your notes securely in your own Google Drive account. 
                  Your data stays private and under your control.
                </p>
              </div>

              <div className="max-w-md mx-auto px-2">
                <CustomerGoogleDriveConnect
                  onConnectionSuccess={handleCloudConnectionSuccess}
                  onConnectionError={handleCloudConnectionError}
                />
              </div>
              
              <div className="text-center mt-4 sm:mt-6">
                <button
                  onClick={nextStep}
                  className="text-xs sm:text-sm text-gray-500 hover:text-gray-700 px-2"
                >
                  Skip for now (you can set this up later in Settings)
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Managed Storage (for Basic/Pro plans) */}
          {currentStep === 3 && (selectedPlan === 'basic' || selectedPlan === 'pro') && (
            <div className="text-center">
              <div className="w-20 h-20 sm:w-24 sm:h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                <Check className="w-10 h-10 sm:w-12 sm:h-12 text-green-600" />
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">Storage Ready!</h2>
              <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6 px-2">
                Your managed storage is configured and ready to use.
              </p>
              <button
                onClick={() => setCurrentStep(4)} // Skip to completion
                className="px-4 sm:px-6 py-2 bg-green-600 text-white text-sm sm:text-base rounded-lg hover:bg-green-700"
              >
                Continue to Final Step
              </button>
            </div>
          )}

          {/* Step 4: Completion */}
          {currentStep === 4 && (
            <div className="text-center">
              <div className="w-20 h-20 sm:w-24 sm:h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                <Check className="w-10 h-10 sm:w-12 sm:h-12 text-green-600" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">
                You're all set!
              </h2>
              <p className="text-base sm:text-lg text-gray-600 mb-4 sm:mb-6 px-2">
                Your notes app is ready to use. Start organizing your thoughts and ideas.
              </p>
              
              <div className="bg-gray-50 rounded-lg p-4 sm:p-6 max-w-md mx-auto mb-4 sm:mb-6">
                <h3 className="font-medium text-gray-900 mb-3 sm:mb-4 text-sm sm:text-base">Your setup:</h3>
                <div className="space-y-2 text-xs sm:text-sm">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Plan:</span>
                    <span className="font-medium text-gray-900 capitalize text-right">
                      {selectedPlan?.replace('_', ' ')} Plan
                    </span>
                  </div>
                  {isConnected && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Storage:</span>
                      <span className="font-medium text-gray-900 capitalize text-right">
                        {connectedProvider?.replace('_', ' ')}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Account:</span>
                    <span className="font-medium text-gray-900 text-right truncate ml-2">{user?.email}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer Navigation */}
        <div className="p-4 sm:p-6 border-t border-gray-200">
          <div className="flex flex-col sm:flex-row items-center justify-between space-y-3 sm:space-y-0">
            <button
              onClick={prevStep}
              disabled={currentStep === 1}
              className={`flex items-center justify-center space-x-2 px-4 py-2 text-sm font-medium rounded-lg w-full sm:w-auto ${
                currentStep === 1 
                  ? 'text-gray-400 cursor-not-allowed' 
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Previous</span>
            </button>

            <div className="flex items-center space-x-3 w-full sm:w-auto">
              {currentStep < steps.length ? (
                <button
                  onClick={nextStep}
                  disabled={!canProceed()}
                  className={`flex items-center justify-center space-x-2 px-4 sm:px-6 py-2 text-sm font-medium rounded-lg w-full sm:w-auto ${
                    canProceed()
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  <span>Continue</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  onClick={completeOnboarding}
                  disabled={isCompleting}
                  className="flex items-center justify-center space-x-2 px-4 sm:px-6 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 w-full sm:w-auto"
                >
                  {isCompleting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Setting up...</span>
                    </>
                  ) : (
                    <>
                      <span>Start using Notes</span>
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default OnboardingPage 